<template>
    <div class="mt-3 rounded-md border border-gray-200 bg-gray-50 p-4">
        <div class="mb-4 flex items-center justify-between">
            <div>
                <h4 class="text-sm font-semibold text-gray-900">
                    Experience Assets
                </h4>

                <p class="text-xs text-gray-500">
                    Assign Lite Integration assets to this experience.
                </p>
            </div>
        </div>

        <div
            v-if="error"
            class="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700"
        >
            {{ error }}
        </div>

        <div class="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div class="md:col-span-2">
                <label class="mb-1 block text-sm font-medium text-gray-700">
                    Asset
                </label>

                <select
                    v-model="form.asset_uuid"
                    class="w-full rounded-md border border-gray-300 px-3 py-2"
                    :disabled="loadingAvailable || saving"
                >
                    <option value="">
                        Select an asset
                    </option>

                    <option
                        v-for="asset in availableAssets"
                        :key="asset.uuid"
                        :value="asset.uuid"
                    >
                        {{ asset.name }}
                    </option>
                </select>
            </div>

            <div>
                <label class="mb-1 block text-sm font-medium text-gray-700">
                    Roblox Asset ID
                </label>

                <input
                    v-model="form.roblox_asset_id"
                    type="text"
                    class="w-full rounded-md border border-gray-300 px-3 py-2"
                    placeholder="Optional"
                    :disabled="saving"
                >
            </div>

            <div>
                <label class="mb-1 block text-sm font-medium text-gray-700">
                    Roblox Placement ID
                </label>

                <input
                    v-model="form.roblox_placement_id"
                    type="text"
                    class="w-full rounded-md border border-gray-300 px-3 py-2"
                    placeholder="Optional"
                    :disabled="saving"
                >
            </div>
        </div>

        <div
            v-if="validationErrors.asset_uuid"
            class="mt-2 text-sm text-red-600"
        >
            {{ validationErrors.asset_uuid[0] }}
        </div>

        <div
            v-if="validationErrors.roblox_asset_id"
            class="mt-2 text-sm text-red-600"
        >
            {{ validationErrors.roblox_asset_id[0] }}
        </div>

        <div
            v-if="validationErrors.roblox_placement_id"
            class="mt-2 text-sm text-red-600"
        >
            {{ validationErrors.roblox_placement_id[0] }}
        </div>

        <div class="mt-3 flex justify-end">
            <button
                type="button"
                class="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="!form.asset_uuid || saving"
                @click="attachAsset"
            >
                {{ saving ? 'Adding...' : 'Add Asset' }}
            </button>
        </div>

        <div
            v-if="loadingAttached"
            class="py-6 text-center text-sm text-gray-500"
        >
            Loading assets...
        </div>

        <div
            v-else-if="!attachedAssets.length"
            class="mt-4 rounded-md border border-gray-200 bg-white p-5 text-center text-sm text-gray-500"
        >
            No assets are assigned to this experience.
        </div>

        <div
            v-else
            class="mt-4 overflow-hidden rounded-md border border-gray-200 bg-white"
        >
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                        <th class="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                            Asset
                        </th>

                        <th class="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                            Roblox Asset ID
                        </th>

                        <th class="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                            Roblox Placement ID
                        </th>

                        <th class="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">
                            Actions
                        </th>
                    </tr>
                </thead>

                <tbody class="divide-y divide-gray-200">
                    <tr
                        v-for="relationship in attachedAssets"
                        :key="relationship.id"
                    >
                        <td class="px-4 py-3">
                            <div class="flex w-24 flex-col items-center">
                                <img
                                    v-if="relationship.asset?.image_url"
                                    :src="relationship.asset.image_url"
                                    :alt="relationship.asset.name"
                                    class="h-16 w-16 rounded-md object-cover"
                                >

                                <div
                                    v-else
                                    class="flex h-16 w-16 items-center justify-center rounded-md bg-gray-100 text-xs text-gray-400"
                                >
                                    No image
                                </div>

                                <span
                                    class="asset-name mt-1 max-w-full text-center text-xs font-medium text-gray-900"
                                    :title="relationship.asset?.name"
                                >
                                    {{ relationship.asset?.name ?? '-' }}
                                </span>
                            </div>
                        </td>

                        <td class="px-4 py-3">
                            <input
                                v-model="relationship.roblox_asset_id"
                                type="text"
                                class="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                                placeholder="None"
                                :disabled="updatingId === relationship.id"
                                @change="updateAsset(relationship)"
                            >
                        </td>

                        <td class="px-4 py-3">
                            <input
                                v-model="relationship.roblox_placement_id"
                                type="text"
                                class="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                                placeholder="None"
                                :disabled="updatingId === relationship.id"
                                @change="updateAsset(relationship)"
                            >
                        </td>

                        <td class="px-4 py-3 text-right">
                            <button
                                type="button"
                                class="rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                                :disabled="deletingId === relationship.id"
                                @click="detachAsset(relationship)"
                            >
                                {{
                                    deletingId === relationship.id
                                        ? 'Removing...'
                                        : 'Remove'
                                }}
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
const emptyForm = () => ({
    asset_uuid: '',
    roblox_asset_id: '',
    roblox_placement_id: '',
});

export default {
    name: 'LiteIntegrationExperienceAssets',

    props: {
        liteIntegration: {
            type: Object,
            required: true,
        },

        liteIntegrationExperience: {
            type: Object,
            required: true,
        },
        assetsRevision: {
            type: Number,
            default: 0,
        },

        experienceAssetsRevision: {
            type: Number,
            required: true,
        },
    },

    watch: {
        assetsRevision() {
            this.loadAvailableAssets();
        },
        
        experienceAssetsRevision() {
            this.load();
        },
    },

    data() {
        return {
            attachedAssets: [],
            availableAssets: [],
            form: emptyForm(),
            validationErrors: {},
            loadingAttached: false,
            loadingAvailable: false,
            saving: false,
            updatingId: null,
            deletingId: null,
            error: null,
        };
    },

    mounted() {
        this.load();
    },

    methods: {
        async load() {
            await Promise.all([
                this.loadAttachedAssets(),
                this.loadAvailableAssets(),
            ]);
        },

        async loadAttachedAssets() {
            this.loadingAttached = true;
            this.error = null;

            try {
                const response = await this.$http.get(
                    this.baseUrl
                );

                this.attachedAssets = response.data.data ?? [];
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to load experience assets.'
                );
            } finally {
                this.loadingAttached = false;
            }
        },

        async loadAvailableAssets() {
            if (this.loadingAvailable) {
                return;
            }

            this.loadingAvailable = true;
            this.error = null;

            try {
                const response = await this.$http.get(
                    `${this.baseUrl}/available`
                );

                this.availableAssets = response.data.data ?? [];
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to load available assets.'
                );
            } finally {
                this.loadingAvailable = false;
            }
        },

        async attachAsset() {
            if (!this.form.asset_uuid) {
                return;
            }

            this.saving = true;
            this.error = null;
            this.validationErrors = {};

            try {
                await this.$http.post(
                    this.baseUrl,
                    {
                        asset_uuid: this.form.asset_uuid,
                        roblox_asset_id:
                            this.form.roblox_asset_id || null,
                        roblox_placement_id:
                            this.form.roblox_placement_id || null,
                    }
                );

                this.form = emptyForm();

                await Promise.all([
                    this.loadAttachedAssets(),
                    this.loadAvailableAssets(),
                ]);
                this.$emit('experience-assets-changed');
            } catch (error) {
                if (error.response?.status === 422) {
                    this.validationErrors =
                        error.response.data.errors ?? {};

                    return;
                }

                this.error = this.getErrorMessage(
                    error,
                    'Unable to attach asset.'
                );
            } finally {
                this.saving = false;
            }
        },

        async updateAsset(relationship) {
            this.updatingId = relationship.id;
            this.error = null;

            try {
                await this.$http.patch(
                    `${this.baseUrl}/${relationship.id}`,
                    {
                        roblox_asset_id:
                            relationship.roblox_asset_id || null,
                        roblox_placement_id:
                            relationship.roblox_placement_id || null,
                    }
                );
                this.$emit('experience-assets-changed');
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to update Roblox IDs.'
                );

                await this.loadAttachedAssets();
            } finally {
                this.updatingId = null;
            }
        },

        async detachAsset(relationship) {
            const assetName =
                relationship.asset?.name ?? 'this asset';

            if (!window.confirm(
                `Remove "${assetName}" from this experience?`
            )) {
                return;
            }

            this.deletingId = relationship.id;
            this.error = null;

            try {
                await this.$http.delete(
                    `${this.baseUrl}/${relationship.id}`
                );

                this.attachedAssets =
                    this.attachedAssets.filter(
                        item => item.id !== relationship.id
                    );

                await this.loadAvailableAssets();
                this.$emit('experience-assets-changed');
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to remove asset.'
                );
            } finally {
                this.deletingId = null;
            }
        },

        getErrorMessage(error, fallback) {
            return error.response?.data?.message ?? fallback;
        },
    },

    computed: {
        baseUrl() {
            return `/lite-integrations/${this.liteIntegration.uuid}`
                + `/experiences/${this.liteIntegrationExperience.uuid}`
                + '/assets';
        },
    },
};
</script>

<style scoped>
.asset-name {
    display: -webkit-box;
    overflow: hidden;
    overflow-wrap: break-word;
    hyphens: auto;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
}
</style>